// src/store/metricsSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getKapabilityData } from "../api/kentyouEye";

// Thunk to fetch all eight (or fewer) metrics whenever IDs change
export const fetchAllMetrics = createAsyncThunk(
  "metrics/fetchAll",
  async (_, { getState, rejectWithValue }) => {
    const state = getState();
    const { gatewayId, watchId } = state.selection;
    const allKoncepts = state.koncepts.items; // array of { id, kapabilities: [{id, ...}], ... }

    if (!gatewayId || !watchId) {
      return rejectWithValue("gatewayId or watchId not set");
    }

    // Helper: check if a kapability exists on a konzept
    function hasKapability(konzeptId, kapId) {
      const kon = allKoncepts.find((k) => k.id === konzeptId);
      return kon?.kapabilities.some((cap) => cap.id === kapId);
    }

    // Define which kapabilities we want to fetch
    const gwCaps = [
      "StatusPRM~uptime",
      "StatusSTS~wifidowntime",
      "StatusPRM~bootcount",
      "StatusPRM~restartreason",
    ].filter((capId) => hasKapability(gatewayId, capId));

    const wtCaps = [
      "steps~data",
      "location~data",
      "heartrate~data",
      "oxymetry~data",
      "batterylevel~data",
    ].filter((capId) => hasKapability(watchId, capId));

    // Launch all fetches in parallel
    const promises = [];

    gwCaps.forEach((cap) => {
      promises.push(
        getKapabilityData(gatewayId, cap).then((json) => ({
          konzeptId: gatewayId,
          kapabilityId: cap,
          data: json.data,
        }))
      );
    });

    wtCaps.forEach((cap) => {
      promises.push(
        getKapabilityData(watchId, cap).then((json) => ({
          konzeptId: watchId,
          kapabilityId: cap,
          data: json.data,
        }))
      );
    });

    try {
      const results = await Promise.all(promises);
      /**
       * results is an array of objects:
       * [ { konzeptId, kapabilityId, data: { id, name, value, timestamp } }, ... ]
       */
      return results;
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

const metricsSlice = createSlice({
  name: "metrics",
  initialState: {
    entries: {},       // { "<konzeptId>~<kapabilityId>": { value, timestamp } }
    status: "idle",
    error: null,
  },
  reducers: {
    clearMetrics(state) {
      state.entries = {};
      state.status = "idle";
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllMetrics.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchAllMetrics.fulfilled, (state, action) => {
        state.status = "succeeded";
        // Build a map: key = `${konzeptId}~${kapabilityId}`
        const map = {};
        action.payload.forEach(({ konzeptId, kapabilityId, data }) => {
          const key = `${konzeptId}~${kapabilityId}`;
          map[key] = {
            value: data.value,
            timestamp: data.timestamp,
          };
        });
        state.entries = map;
      })
      .addCase(fetchAllMetrics.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      });
  },
});

export const { clearMetrics } = metricsSlice.actions;
export default metricsSlice.reducer;

/**
 * Helper selectors:
 *   - selectMetric(gatewayId, "StatusPRM~uptime")
 *   - selectMetric(watchId, "steps~data")
 */
export const selectMetric = (state, konzeptId, kapabilityId) =>
  state.metrics.entries[`${konzeptId}~${kapabilityId}`];
